import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { cn } from '../../../components/ui/Button';
import { Button } from '../../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import {
  calculateBookingFinancials,
  calculateSummaryFinancials,
  calculateBarberBreakdown,
  TRANSPORT_FEE,
  SERVICE_CHARGE_RATE,
  BARBER_SHARE_RATE,
  OWNER_SHARE_RATE,
} from '../../../utils/financialCalculations';
import { financialReportService } from '../../../services/financialReportService';

const { FiDownload, FiChevronDown, FiFileText, FiTable, FiCalendar, FiInfo } = FiIcons;

export default function AdminPayments() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [timeFilter]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowExportOptions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    let query = supabase
      .from('bookings_1740423033529')
      .select('*, barber:barber_id(full_name), customer:customer_id(full_name), service:service_id(name)')
      .order('created_at', { ascending: false });

    if (timeFilter) {
      const start = new Date(`${timeFilter}-01T00:00:00Z`);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
      query = query.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
    }

    const { data } = await query;
    setBookings(data || []);
    setLoading(false);
  };

  const summary = calculateSummaryFinancials(bookings);
  const barberBreakdown = calculateBarberBreakdown(bookings);

  const reportTitle = timeFilter
    ? `Financial Report — ${new Date(timeFilter + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}`
    : 'Financial Report — All Time';
  const fileName = `BookOTG_Financial_${timeFilter || 'AllTime'}`;

  const handleExport = (format) => {
    if (format === 'pdf') {
      financialReportService.exportToPDF(bookings, summary, barberBreakdown, reportTitle, fileName);
    } else {
      financialReportService.exportToExcel(bookings, summary, barberBreakdown, fileName);
    }
    setShowExportOptions(false);
  };

  const SummaryCard = ({ label, value, sub, color = 'text-brand-dark', bg = 'bg-white', icon }) => (
    <div className={`${bg} p-6 rounded-[2rem] border border-gray-100 shadow-sm`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg === 'bg-white' ? 'bg-gray-50' : 'bg-white/10'}`}>
          <SafeIcon name={icon} className={`text-lg ${color}`} />
        </div>
      </div>
      <p className={`text-2xl font-black tracking-tight ${color}`}>{value}</p>
      <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 font-medium mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Financial Ecosystem</h1>
          <p className="text-gray-500 font-medium italic mt-1">Complete revenue breakdown & payout management.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Formula Info */}
          <button
            onClick={() => setShowFormulaModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-brand-primary border border-blue-100 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-100 transition-colors"
          >
            <SafeIcon icon={FiInfo} className="text-sm" />
            Fee Formula
          </button>

          {/* Month Filter */}
          <div className="flex items-center gap-2 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
            <SafeIcon icon={FiCalendar} className="text-gray-400 ml-1" />
            <input
              type="month"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="border-none outline-none bg-transparent font-black text-brand-dark text-sm cursor-pointer"
            />
            {timeFilter && (
              <button onClick={() => setTimeFilter('')} className="text-[10px] text-brand-primary font-black px-2 hover:underline">
                Clear
              </button>
            )}
          </div>

          {/* Export */}
          <div className="relative" ref={dropdownRef}>
            <Button
              onClick={() => setShowExportOptions(!showExportOptions)}
              className="h-10 px-5 bg-brand-dark text-white text-[10px] font-black uppercase tracking-widest rounded-xl flex items-center gap-2"
            >
              <SafeIcon icon={FiDownload} />
              Export Report
              <SafeIcon icon={FiChevronDown} className={showExportOptions ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </Button>

            {showExportOptions && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2 overflow-hidden animate-in slide-in-from-top-2">
                <button onClick={() => handleExport('pdf')} className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50 flex items-center gap-3">
                  <SafeIcon icon={FiFileText} className="text-red-500 text-lg" />
                  Download PDF
                </button>
                <button onClick={() => handleExport('xlsx')} className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50 flex items-center gap-3">
                  <SafeIcon icon={FiTable} className="text-green-600 text-lg" />
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-white p-1 rounded-2xl border border-gray-100 shadow-sm mb-8 w-fit">
        {['overview', 'barbers', 'ledger'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all',
              activeTab === tab ? 'bg-brand-dark text-white' : 'text-gray-500 hover:text-brand-dark'
            )}
          >
            {tab === 'overview' ? 'Overview' : tab === 'barbers' ? 'Barber Payouts' : 'Ledger'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-20 text-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">Calculating financials...</p>
        </div>
      ) : (
        <>
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-8 animate-in fade-in">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="bg-brand-dark p-6 rounded-[2rem] text-white shadow-xl col-span-1 sm:col-span-2 lg:col-span-1">
                  <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-2">Gross Revenue (Completed)</p>
                  <p className="text-3xl font-black">Rs. {summary.grossRevenue.toLocaleString()}</p>
                  <p className="text-gray-400 text-xs font-bold mt-2">{summary.completedCount} completed bookings</p>
                </div>
                <SummaryCard
                  label="Transport Fees (to Barbers)"
                  value={`Rs. ${summary.totalTransportFees.toLocaleString()}`}
                  icon="Truck"
                  sub="Rs. 1,500 fixed per booking"
                />
                <SummaryCard
                  label="Service Charges (4% Hosting)"
                  value={`Rs. ${summary.totalServiceCharges.toLocaleString()}`}
                  icon="Server"
                  sub="4% of net revenue"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <SummaryCard
                  label="Total Distributable"
                  value={`Rs. ${summary.totalDistributable.toLocaleString()}`}
                  icon="DollarSign"
                  sub="After transport + service charge"
                />
                <div className="bg-green-50 p-6 rounded-[2rem] border border-green-100 shadow-sm">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mb-3">
                    <SafeIcon name="Users" className="text-green-600 text-lg" />
                  </div>
                  <p className="text-2xl font-black text-green-700">Rs. {summary.totalBarberEarnings.toLocaleString()}</p>
                  <p className="text-[10px] text-green-600 font-black uppercase tracking-widest mt-1">Total Barber Payouts</p>
                  <p className="text-xs text-green-500 font-medium mt-1">Transport + 80% Share</p>
                </div>
                <div className="bg-purple-50 p-6 rounded-[2rem] border border-purple-100 shadow-sm">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                    <SafeIcon name="Crown" className="text-purple-600 text-lg" />
                  </div>
                  <p className="text-2xl font-black text-purple-700">Rs. {summary.totalOwnerCommission.toLocaleString()}</p>
                  <p className="text-[10px] text-purple-600 font-black uppercase tracking-widest mt-1">Fernando's Commission (20%)</p>
                  <p className="text-xs text-purple-500 font-medium mt-1">Owner's platform earnings</p>
                </div>
              </div>

              {/* Formula Visual */}
              <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-8">
                <h3 className="text-lg font-black text-brand-dark mb-6 flex items-center gap-2">
                  <SafeIcon name="Calculator" className="text-brand-primary" />
                  Revenue Distribution Formula
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
                  {[
                    { label: 'Service Price', val: 'X LKR', color: 'bg-gray-50 border-gray-200', text: 'text-brand-dark' },
                    { label: '− Transport Fee', val: 'Rs. 1,500', color: 'bg-orange-50 border-orange-100', text: 'text-orange-600', arrow: true },
                    { label: '− Service Charge', val: '4% of Net', color: 'bg-yellow-50 border-yellow-100', text: 'text-yellow-700', arrow: true },
                    { label: 'Barber Gets', val: 'Transport + 80%', color: 'bg-green-50 border-green-100', text: 'text-green-700', arrow: true },
                    { label: 'Fernando Gets', val: '20% of Dist.', color: 'bg-purple-50 border-purple-100', text: 'text-purple-700', arrow: true },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {step.arrow && <SafeIcon name="ArrowRight" className="text-gray-300 flex-shrink-0 hidden md:block" />}
                      <div className={`flex-1 p-4 rounded-2xl border ${step.color} text-center`}>
                        <p className={`text-sm font-black ${step.text}`}>{step.val}</p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">{step.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* BARBER PAYOUTS TAB */}
          {activeTab === 'barbers' && (
            <div className="animate-in fade-in">
              {barberBreakdown.length === 0 ? (
                <div className="bg-white rounded-[2rem] p-16 text-center border border-dashed border-gray-200">
                  <SafeIcon name="Users" className="text-gray-300 text-3xl mx-auto mb-4" />
                  <p className="text-gray-400 font-bold">No completed bookings yet.</p>
                </div>
              ) : (
                <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                  <div className="p-6 bg-gray-50/50 border-b border-gray-100">
                    <h3 className="font-black text-brand-dark">Per-Barber Payout Summary</h3>
                    <p className="text-xs text-gray-400 font-medium mt-1">Based on completed bookings only. Transport fee included in total payout.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-gray-400 text-[10px] uppercase tracking-widest font-black bg-gray-50/30 border-b border-gray-50">
                          <th className="px-6 py-4">Barber</th>
                          <th className="px-6 py-4 text-right">Bookings</th>
                          <th className="px-6 py-4 text-right">Gross Revenue</th>
                          <th className="px-6 py-4 text-right">Transport (Rs.1500×)</th>
                          <th className="px-6 py-4 text-right">Svc Charge (4%)</th>
                          <th className="px-6 py-4 text-right">Barber Share (80%)</th>
                          <th className="px-6 py-4 text-right bg-green-50">Total Payout</th>
                          <th className="px-6 py-4 text-right bg-purple-50">Owner Cut (20%)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {barberBreakdown.map((b, i) => (
                          <tr key={i} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-brand-primary/10 rounded-xl flex items-center justify-center">
                                  <SafeIcon name="User" className="text-brand-primary text-sm" />
                                </div>
                                <span className="font-black text-brand-dark text-sm">{b.barberName}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right font-bold text-gray-600">{b.bookingCount}</td>
                            <td className="px-6 py-4 text-right font-bold text-brand-dark">Rs. {b.grossRevenue.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right font-bold text-orange-600">Rs. {b.transportFees.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right font-bold text-yellow-600">Rs. {b.serviceCharges.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right font-bold text-gray-700">Rs. {b.barberShare.toLocaleString()}</td>
                            <td className="px-6 py-4 text-right bg-green-50">
                              <span className="font-black text-green-700 text-sm">Rs. {b.totalBarberEarnings.toLocaleString()}</span>
                            </td>
                            <td className="px-6 py-4 text-right bg-purple-50">
                              <span className="font-black text-purple-700 text-sm">Rs. {b.ownerCommission.toLocaleString()}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-brand-dark text-white">
                          <td className="px-6 py-4 font-black text-sm">TOTALS</td>
                          <td className="px-6 py-4 text-right font-black">{summary.completedCount}</td>
                          <td className="px-6 py-4 text-right font-black">Rs. {summary.grossRevenue.toLocaleString()}</td>
                          <td className="px-6 py-4 text-right font-black text-orange-300">Rs. {summary.totalTransportFees.toLocaleString()}</td>
                          <td className="px-6 py-4 text-right font-black text-yellow-300">Rs. {summary.totalServiceCharges.toLocaleString()}</td>
                          <td className="px-6 py-4 text-right font-black text-gray-300">Rs. {summary.totalBarberShare.toLocaleString()}</td>
                          <td className="px-6 py-4 text-right font-black text-green-300">Rs. {summary.totalBarberEarnings.toLocaleString()}</td>
                          <td className="px-6 py-4 text-right font-black text-purple-300">Rs. {summary.totalOwnerCommission.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LEDGER TAB */}
          {activeTab === 'ledger' && (
            <div className="animate-in fade-in">
              <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 bg-gray-50/50 border-b border-gray-100">
                  <h3 className="font-black text-brand-dark">Transaction Ledger</h3>
                  <p className="text-xs text-gray-400 font-medium mt-1">Full per-booking financial breakdown. Completed bookings only.</p>
                </div>
                <div className="overflow-x-auto">
                  {bookings.filter(b => b.status === 'completed').length === 0 ? (
                    <div className="p-16 text-center text-gray-400 italic">No completed transactions yet.</div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-gray-400 text-[9px] uppercase tracking-widest font-black bg-gray-50/50 border-b border-gray-50">
                          <th className="px-4 py-4">Ref</th>
                          <th className="px-4 py-4">Date</th>
                          <th className="px-4 py-4">Barber</th>
                          <th className="px-4 py-4">Service</th>
                          <th className="px-4 py-4 text-right">Gross</th>
                          <th className="px-4 py-4 text-right">Transport</th>
                          <th className="px-4 py-4 text-right">Net Rev.</th>
                          <th className="px-4 py-4 text-right">Svc Chrg</th>
                          <th className="px-4 py-4 text-right">Distributable</th>
                          <th className="px-4 py-4 text-right">Barber Share</th>
                          <th className="px-4 py-4 text-right bg-green-50">Total Payout</th>
                          <th className="px-4 py-4 text-right bg-purple-50">Owner (20%)</th>
                          <th className="px-4 py-4 text-center">Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {bookings
                          .filter(b => b.status === 'completed')
                          .map(b => {
                            const f = calculateBookingFinancials(b.total_price);
                            return (
                              <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 font-mono text-[10px] text-gray-400">{b.id.slice(0, 8).toUpperCase()}</td>
                                <td className="px-4 py-3 text-xs font-bold text-gray-600">{b.booking_date}</td>
                                <td className="px-4 py-3 font-bold text-brand-dark text-xs">{b.barber?.full_name || '—'}</td>
                                <td className="px-4 py-3 text-xs text-gray-500">{b.service?.name || '—'}</td>
                                <td className="px-4 py-3 text-right font-black text-brand-dark text-xs">Rs.{f.grossAmount.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-orange-600 font-bold text-xs">Rs.{f.transportFee.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-600 font-bold text-xs">Rs.{f.netRevenue.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-yellow-600 font-bold text-xs">Rs.{f.serviceCharge.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-700 font-bold text-xs">Rs.{f.distributable.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-700 font-bold text-xs">
                                  Rs.{f.barberShare.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right bg-green-50">
                                  <span className="font-black text-green-700 text-xs">Rs.{f.totalBarberEarnings.toLocaleString()}</span>
                                </td>
                                <td className="px-4 py-3 text-right bg-purple-50">
                                  <span className="font-black text-purple-700 text-xs">Rs.{f.ownerShare.toLocaleString()}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={cn(
                                    'px-2 py-1 rounded-full text-[9px] font-black uppercase border',
                                    b.payment_status === 'paid' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-orange-50 text-orange-600 border-orange-100'
                                  )}>
                                    {b.payment_status === 'paid' ? 'Paid' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Formula Modal */}
      {showFormulaModal && (
        <div className="fixed inset-0 bg-brand-dark/70 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-8 shadow-2xl animate-in zoom-in-95">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-brand-dark">Revenue Split Formula</h2>
              <button onClick={() => setShowFormulaModal(false)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <SafeIcon name="X" className="text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              {[
                { step: '1', label: 'Gross Service Price', formula: 'Customer pays this amount', example: 'Rs. 3,500', color: 'bg-gray-50 border-gray-100', text: 'text-brand-dark' },
                { step: '2', label: 'Deduct Transport Fee', formula: 'Rs. 1,500 fixed — paid to barber for travel', example: '− Rs. 1,500', color: 'bg-orange-50 border-orange-100', text: 'text-orange-700' },
                { step: '3', label: 'Net Revenue', formula: 'Gross − Transport', example: '= Rs. 2,000', color: 'bg-blue-50 border-blue-100', text: 'text-blue-700' },
                { step: '4', label: 'Service Charge (4%)', formula: '4% of Net Revenue — platform hosting & management', example: '− Rs. 80', color: 'bg-yellow-50 border-yellow-100', text: 'text-yellow-700' },
                { step: '5', label: 'Distributable Amount', formula: 'Net Revenue − Service Charge', example: '= Rs. 1,920', color: 'bg-gray-50 border-gray-200', text: 'text-brand-dark' },
                { step: '6', label: 'Barber Share (80%)', formula: '80% of Distributable amount', example: '= Rs. 1,536', color: 'bg-gray-50 border-gray-100', text: 'text-green-700' },
                { step: '7', label: "Fernando's Commission (20%)", formula: '20% of Distributable amount', example: '= Rs. 384', color: 'bg-purple-50 border-purple-100', text: 'text-purple-700' },
                { step: '8', label: "Total Barber Payout", formula: 'Transport + Barber Share', example: '= Rs. 3,036', color: 'bg-green-50 border-green-100', text: 'text-green-700' },
              ].map(item => (
                <div key={item.step} className={`flex items-center gap-4 p-4 rounded-2xl border ${item.color}`}>
                  <div className="w-8 h-8 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100">
                    <span className="text-xs font-black text-brand-primary">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <p className={`font-black text-sm ${item.text}`}>{item.label}</p>
                    <p className="text-xs text-gray-400 font-medium mt-0.5">{item.formula}</p>
                  </div>
                  <span className={`font-black text-sm ${item.text} flex-shrink-0`}>{item.example}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-brand-primary/5 rounded-2xl border border-brand-primary/10">
              <p className="text-xs text-brand-primary font-bold text-center">
                Example: Rs. 3,500 service → Barber receives Rs. 1,536 (Share) + Rs. 1,500 (Transport) = Rs. 3,036 total
              </p>
            </div>

            <Button className="w-full mt-6" onClick={() => setShowFormulaModal(false)}>
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}